/* ELC Shift — Auth & boot module (login, app start, idle/session, config + staff loading, topbar/sync).
   Extracted from index.html block #22 (Sprint 4) via AST. Behaviour-preserving: each function was a
   global inline declaration, so all are re-exposed on window. The bundle loads BEFORE block #22, so
   window.startApp/doLogin/loadAppConfig… exist before block #22 top-level boot code runs or the
   window load event fires. Reads app globals (DB, currentStore, staffData, ALL_STAFF…) at runtime. */
import { lsSetSafe } from '../storage/quota.js';   // v129.99 — quota self-healing (Dar Al Salam "storage full")
import { screenFromSearch } from './deep-link.js';   // v132.65 - ?screen=training lands the person on that tab
import { rotaStoreFromSearch } from './deep-link.js';   // v136.3 - ?rotastore=NAME, only a store this person may pick
import { weekendFor } from '../shared/rota-country.js';   // v133.94: one Qatar weekend
(function(){

function loadAppConfig() {
  var tp = {};
  try { tp = JSON.parse(localStorage.getItem('elc_trading_pattern') || '{}'); } catch(e) {}

  // Opening hours — from admin Stores → Opening Hours tab
  var storedHours = {};
  try { storedHours = JSON.parse(localStorage.getItem('elc_opening_hours') || '{}'); } catch(e) {}

  return {
    // Weekend days (no D/O allowed, protected days)
    uaeWeekend:     tp.uaeWeekend     || ['Saturday','Sunday'],
    qatarWeekend:   weekendFor(true, tp),   // v133.94: the shared answer (a saved old Thursday-Friday reads as Friday-Saturday)
    // Peak days (max staffing)
    peakDays:       tp.peakDays       || ['Thursday','Friday','Saturday','Sunday'],
    // Quiet days (best for D/O)
    quietDays:      tp.quietDays      || ['Monday','Tuesday'],
    // Sales split
    morningSplit:   tp.morningSplit   || 50,
    // Max hours
    maxHours:       tp.maxHours       || 48,
    // Lunch stagger
    lunchStagger:   tp.lunchStagger   || 30,
    // Opening hours
    weekdayOpen:    storedHours.wdOpen  || '09:00',
    weekdayClose:   storedHours.wdClose || '22:00',
    weekendOpen:    storedHours.weOpen  || '09:00',
    weekendClose:   storedHours.weClose || '24:00',
    // Qatar-specific
    fridayQatar:    tp.fridayQatar    || 'Friday morning in Qatar is quiet until after prayer time (~13:30).',
    otherNotes:     tp.otherNotes     || '',
  };
}

function mapStaffFromDB(row) {
  return {
    id: row.id, name: row.name, role: row.role, store: row.store_name || row.store,
    area: row.area_id || row.area_name || row.area, country: row.country, color: row.color || 'var(--brand,#009E3D)',
    dayOff: row.day_off || row.dayOff || null, toilBalance: row.toil_balance || row.toilBalance || 0,
    weekHours: row.week_hours || row.weekHours || 0, active: row.active !== false,
    contractedHours: row.contracted_hours || row.contractedHours || 48,
    // v59: pin itself is no longer returned by the worker — only pin_length.
    // Keep the field so the PIN pad knows how many digits to expect for auto-submit.
    pin_length: row.pin_length || 0,
    // v59: privacy-notice consent state. MUST be preserved here — without it,
    // any silent refresh (refreshDataSilently) blanks out the acknowledgement
    // in memory and re-prompts the staff on next clock-in. The server still
    // remembers, but the client forgets.
    consent_version:         row.consent_version         || null,
    consent_acknowledged_at: row.consent_acknowledged_at || null,
    consent_lang:            row.consent_lang            || null,
    // v133.116: other stores a store manager also looks after, so they can sign in to Develop on that
    // store's device (src/mydev). Absent until migration v237-0 runs, so [] by default.
    alsoManages: Array.isArray(row.also_manages) ? row.also_manages : [],
  };
}

// v136.8 (boot audit, 16 Oct 2026): the boot asks once and in parallel.
//   - /api/sync already returns stores, holidays, checklist templates, accounts and
//     areas (worker.js, the syncJobs list: the SAME queries as /api/stores,
//     /api/holidays, /api/checklist-templates, /api/accounts and /api/areas, the
//     same public shapes). They are taken from it instead of being asked for again.
//     /api/stores adds one field, password_set, which the staff app never reads.
//   - A slice is taken from the sync only when the sync was LIVE (not the offline
//     cache copy) and that slice was not degraded (the Worker returns [] and names
//     it in _degraded when one table failed). Otherwise the old separate read runs,
//     with its old fallback, so a single failed read still boots exactly as before.
//   - The two app_config keys (default_opening_hours, trading_pattern) are NOT in the
//     sync payload (its trading_pattern is a different TABLE), so they are still read,
//     now alongside balances and targets instead of one after another.
//   - index.html starts DB.sync() while the page is still loading (window.__bootSyncP);
//     the first call here uses that request rather than starting another.
var _liveSync = null;          // the last live /api/sync payload this page received
var _bootSyncUsed = false;     // the early boot request is used once, by the first load
function bootSyncPromise() {
  try { return (typeof window !== 'undefined' && window.__bootSyncP) || null; } catch (e) { return null; }
}
function syncSlice(data, key) {
  if (!data || data.offline || !Array.isArray(data[key])) return null;
  if (Array.isArray(data._degraded) && data._degraded.indexOf(key) !== -1) return null;
  return data[key];
}
// The saved session's store (the boot runs before index.html restores the session
// into currentStore). Used only to name the store for the balances read at boot.
function savedSessionStore() {
  try {
    if (localStorage.getItem('elcShiftVersion') !== 'v49') return undefined;
    var u = JSON.parse(localStorage.getItem('elcShiftUser') || 'null');
    return (u && u.store) ? String(u.store) : undefined;
  } catch (e) { return undefined; }
}

async function loadAllStaffFromDB() {
  var data = null;
  try {
    var early = (!_bootSyncUsed) ? bootSyncPromise() : null;
    _bootSyncUsed = true;
    data = await (early || DB.sync());
    if (data && !data.offline) _liveSync = data;
    if (data && data.staff && data.staff.length) {
      ALL_STAFF = data.staff.map(mapStaffFromDB);
      dlog('[DB] Loaded ' + ALL_STAFF.length + ' staff from database');
    } else {
      throw new Error('Empty staff list from DB');
    }
  } catch(e) {
    console.warn('[DB] Staff load failed, trying localStorage fallback:', e.message);
    try {
      var saved = localStorage.getItem('db_staff');
      if (saved) ALL_STAFF = JSON.parse(saved);
    } catch(e2) {}
  }
  // Phase C3.1 (v130.27): brand the LOGIN SCREEN too, not only the post-login
  // app. The Toyshop Land acceptance is "skin end to end: login, staff app,
  // admin console" - and until now nothing called the applier before startApp,
  // so a tenant device sat on an ELC-green login. org_config is cached by the
  // DB.sync() that just resolved above; for ELC (no config) this is a no-op.
  try { applyTenantBranding(); } catch (e) { /* cosmetic - never block boot */ }
  // v136.8: everything below is independent of everything else below, so it runs
  // in parallel. Each part keeps its own error handling and fallback (none of them
  // rejects), and the boot waits for all of them, as it waited for each in turn.
  await Promise.allSettled([
    loadOpeningHoursConfig(),
    loadTradingPatternConfig(),
    // v80.16: balance fetch extracted into refreshBalances() so it can be
    // re-called after shift saves (rota change → balance change in the new
    // computed model). Initial load happens here at startup.
    // v136.8: at boot currentStore is not set yet, so the saved session names the store.
    refreshBalances(savedSessionStore()),
    loadHolidays(data),
    // v80.16: Fetch stores metadata for the new stores-driven getMyStores().
    // This is what fixes the area-manager-can't-see-store-with-no-staff bug
    // class. Each store carries its area_id, which getMyStores() now uses
    // directly instead of inferring from staff records.
    //
    // v85.0: Also retain upload_name (the Top 50 spreadsheet key alias).
    // findTop50StoreKey in shared.js needs this to resolve canonical store
    // names to their Top 50 file keys when the spreadsheet uses a different
    // form (e.g. "...COMMUNITY CENTRE" suffix added by the source system).
    loadStoresFromDB(syncSlice(data, 'stores')),
    loadChecklistTemplates(data),
    loadTargets(),
  ]);
}

// Fetch global opening hours config
async function loadOpeningHoursConfig() {
  try {
    var hrsCfg = await DB.config.get('default_opening_hours');
    if (hrsCfg && hrsCfg.value) {
      lsSetSafe('elc_opening_hours', JSON.stringify(hrsCfg.value));
    }
  } catch(e) { /* no global hours config — use defaults */ }
}
// Fetch trading pattern config
async function loadTradingPatternConfig() {
  try {
    var tpCfg = await DB.config.get('trading_pattern');
    if (tpCfg && tpCfg.value) {
      lsSetSafe('elc_trading_pattern', JSON.stringify(tpCfg.value));
    }
  } catch(e) { /* no trading pattern yet */ }
}
// Fetch public holidays from DB (written by admin via Public Holidays tab).
// v136.8: from the sync payload when it carried them (same query as /api/holidays).
async function loadHolidays(data) {
  try {
    var hRows = syncSlice(data, 'holidays');
    if (!hRows) hRows = await DB.holidays.list();
    if (Array.isArray(hRows) && hRows.length > 0) {
      var grouped = { UAE: [], Qatar: [] };
      hRows.forEach(function(r){
        var country = r.country || 'UAE';
        if (grouped[country]) grouped[country].push({ id: r.id, date: r.date, name: r.name });
      });
      lsSetSafe('elc_holidays_data', JSON.stringify(grouped));
      dlog('[DB] Loaded ' + hRows.length + ' public holidays');
    }
  } catch(e) { /* fall back to hardcoded PUBLIC_HOLIDAYS */ }
}
// Fetch checklist templates from DB.
// v136.8: from the sync payload when it carried them (same query as /api/checklist-templates).
async function loadChecklistTemplates(data) {
  try {
    var tmplRows = syncSlice(data, 'checklist_templates');
    if (!tmplRows) tmplRows = await DB.checklistTemplates.list();
    if (Array.isArray(tmplRows)) {
      tmplRows.forEach(function(r){
        // Only override if DB actually has content (items is non-empty array)
        if (r.type && r.items && Array.isArray(r.items) && r.items.length > 0) {
          lsSetSafe('elc_checklist_template_' + r.type, JSON.stringify(r.items));
        }
      });
    }
  } catch(e) { /* fall back to localStorage / defaults */ }
}
// Fetch targets from DB — delegated to targets.js module
// (v136.8: a store device reads its store and the weeks its screens show; see targets.js).
async function loadTargets() {
  try {
    var loaded = await TARGETS.loadFromDB();
    if (loaded) dlog('[DB] Loaded ' + loaded + ' target rows');
  } catch(e) { /* no targets yet — use built-in */ }
}

// Slice 1 (load speed): the stores fetch, split out of loadAllStaffFromDB so the login
// store picker can be populated from this lightweight /api/stores list FIRST, independent
// of the heavier full-staff sync. Still re-run inside loadAllStaffFromDB (and
// refreshDataSilently) so the stores list stays fresh on tab-return.
//
// v80.16 + v85.0: each store carries area_id (drives getMyStores) and upload_name (the
// Top 50 spreadsheet key alias used by findTop50StoreKey).
//
// v136.8: loadAllStaffFromDB passes the stores the sync payload already carried
// (the same active stores, same order); with none, it reads /api/stores as before.
async function loadStoresFromDB(syncRows) {
  try {
    var storeRows = Array.isArray(syncRows) ? syncRows : await DB.stores.list();
    if (Array.isArray(storeRows)) {
      ALL_STORES = storeRows
        .filter(function(s){ return s.active !== false; })
        .map(function(s){ return { name: s.name, area_id: s.area_id, country: s.country, active: s.active !== false, upload_name: s.upload_name || null }; });
      dlog('[DB] Loaded ' + ALL_STORES.length + ' active stores');
    }
  } catch(e) { /* fall back to ALL_STAFF-derived store list inside getMyStores */ }
}

// v136.8: the login picker's Area & Head Office group reads accounts + areas. The
// boot's /api/sync carries both (accounts through the same fetchAccountsSafe and
// public strip as /api/accounts; areas with the same order=id), so they are taken
// from it: the live payload this page already has, else the boot request still in
// flight. null for either means "read it as before".
async function loginRefFromSync() {
  var d = _liveSync;
  if (!d && bootSyncPromise()) {
    try { d = await bootSyncPromise(); } catch (e) { d = null; }
  }
  return { accounts: syncSlice(d, 'accounts'), areas: syncSlice(d, 'areas') };
}

async function populateLoginStores(){
  var sel=document.getElementById('loginStore');
  if(!sel)return;
  // Prefer the lightweight ALL_STORES list (from /api/stores) so the picker is usable the
  // instant stores load — independent of the heavier full-staff sync. Fall back to deriving
  // the list from ALL_STAFF (legacy) only if stores haven't loaded yet.
  var stores;
  if (Array.isArray(ALL_STORES) && ALL_STORES.length) {
    stores = ALL_STORES.filter(function(s){return s.active!==false;}).map(function(s){return s.name;});
  } else if (Array.isArray(ALL_STAFF) && ALL_STAFF.length) {
    stores = [...new Set(ALL_STAFF.filter(function(s){return s.active!==false;}).map(function(s){return s.store;}))];
  } else {
    sel.innerHTML = '<option value="">Loading stores…</option>';
    sel.disabled = true;
    return;
  }
  sel.disabled = false;
  stores = [...new Set(stores)].sort();
  if(!stores.includes('Retail Relief Managers'))stores.push('Retail Relief Managers');
  // v131.46: index.html calls this THREE times during boot (placeholder, after
  // the store list, after the staff list). On a slow till the last rebuild
  // landed while a manager was mid-way through the reset-password modal and
  // blanked the store they had picked - the Worker then said "store ...
  // required" (an area manager, Spinneys Umm Suqueim, 11 Sep). Keep the selection.
  var keepSel = sel.value;
  sel.innerHTML='<option value="">— choose —</option>';
  var uae=stores.filter(function(s){return !s.startsWith('Doha')&&s!=='Retail Relief Managers';});
  var qatar=stores.filter(function(s){return s.startsWith('Doha');});
  var virtual=stores.filter(function(s){return s==='Retail Relief Managers';});
  function addGroup(label,list){
    if(!list.length)return;
    var grp=document.createElement('optgroup');grp.label=label;
    list.forEach(function(st){var o=document.createElement('option');o.value=st;o.textContent=st;grp.appendChild(o);});
    sel.appendChild(grp);
  }
  addGroup('🇦🇪 UAE',uae);
  addGroup('🇶🇦 Qatar',qatar);
  addGroup('🏢 Group',virtual);
  if (keepSel && Array.prototype.some.call(sel.options, function(o){ return o.value === keepSel; })) sel.value = keepSel;   // v131.46

  // v81.4 — Area & Head Office options now sourced live from the
  // accounts table (via /api/accounts) rather than hardcoded. Previously
  // the list was hand-maintained, which left a former admin visible after he'd
  // been removed as an admin and meant his replacement (an area manager) was
  // missing entirely. Auth still happens server-side against the same
  // accounts table on submit — see /api/admin/login. Each account must
  // have a password set in admin → People / Accounts.
  //
  // Filter rule: role='area_manager' AND active=true, plus the special
  // generic 'Head Office' account. The named Head Office staff (the ops manager,
  // the Amanah data contact, the General Manager, etc.) log into the admin panel directly rather
  // than via the staff app, so they're intentionally excluded here.
  //
  // Caller doesn't await this function (line ~2403); the area dropdown
  // gets the store optgroups synchronously and the area/HO optgroup is
  // appended a moment later. Both DB.accounts.list() and DB.areas.list()
  // are cached via fetchWithCache so warm loads are instant.
  //
  // Graceful fallback: if either fetch fails, the stores-only dropdown
  // stands. Better than blocking login on a side-fetch.
  try {
    // v136.8: from the boot's /api/sync when it carried them (see loginRefFromSync); the
    // two reads below are the fallback, exactly as they were.
    var ref = await loginRefFromSync();
    var accountsP = ref.accounts ? Promise.resolve(ref.accounts)
      : (typeof DB !== 'undefined' && DB.accounts && DB.accounts.list)
      ? DB.accounts.list() : Promise.resolve([]);
    var areasP    = ref.areas ? Promise.resolve(ref.areas)
      : (typeof DB !== 'undefined' && DB.areas    && DB.areas.list)
      ? DB.areas.list()    : Promise.resolve([]);
    var pair = await Promise.all([accountsP, areasP]);
    var accounts = Array.isArray(pair[0]) ? pair[0] : [];
    var areas    = Array.isArray(pair[1]) ? pair[1] : [];

    // areaById[areaId] = areaName for the dropdown-label lookup.
    var areaById = {};
    areas.forEach(function(a){ areaById[String(a.id)] = a.name; });

    var areaMgrs = accounts
      .filter(function(a){ return a.role === 'area_manager' && a.active !== false; })
      .sort(function(a, b){ return (a.name||'').localeCompare(b.name||''); });
    var headOffice = accounts.find(function(a){
      return a.name === 'Head Office' && a.role === 'admin' && a.active !== false;
    });
    // v125.47 (Matt: "only a head office account — it needs to show all
    // accounts set up"): named Head-Office people were deliberately EXCLUDED
    // in v81.4, when they only ever used the admin panel. The comms platform
    // changes that — every HO person now logs into the staff app for chat,
    // News and Tasks — so ALL active admin/ho_staff accounts appear. Auth is
    // unchanged: each account still needs its own password (set in admin →
    // People → Accounts) and is verified server-side on submit.
    var hoPeople = accounts
      .filter(function(a){
        return a.active !== false && (a.role === 'admin' || a.role === 'ho_staff') && a.name !== 'Head Office';
      })
      .sort(function(a, b){ return (a.name||'').localeCompare(b.name||''); });

    if (areaMgrs.length || headOffice || hoPeople.length) {
      var mgmtGrp = document.createElement('optgroup');
      mgmtGrp.label = '📋 Area & Head Office';
      areaMgrs.forEach(function(acct){
        var firstName = (acct.name || '').split(' ')[0];
        var areaName  = areaById[String(acct.area)] || '';
        var label = firstName + "'s Area" + (areaName ? ' — ' + areaName : '');
        var o = document.createElement('option');
        o.value = '@acct:' + acct.name;
        o.textContent = label;
        mgmtGrp.appendChild(o);
      });
      if (headOffice) {
        var oHO = document.createElement('option');
        oHO.value = '@acct:Head Office';
        oHO.textContent = 'Head Office — All Stores';
        mgmtGrp.appendChild(oHO);
      }
      hoPeople.forEach(function(acct){
        var o2 = document.createElement('option');
        o2.value = '@acct:' + acct.name;
        o2.textContent = acct.name + ' — Head Office';
        mgmtGrp.appendChild(o2);
      });
      sel.appendChild(mgmtGrp);
    }
  } catch (e) {
    console.warn('[login] could not load Area & Head Office options:', e && e.message);
    // Fall through — stores-only dropdown is acceptable.
  }
}

async function onStoreSelectChange() {
  var val = document.getElementById('loginStore').value;
  // Sync hidden username mirror for Chrome password manager
  if (typeof syncLoginUsername === 'function') syncLoginUsername();
  var passRow = document.getElementById('storePassRow');
  var passInput = document.getElementById('storePassInput');
  var passErr = document.getElementById('storePassErr');
  passErr.style.display = 'none';
  passErr.style.color = '';                       // v131.37: clear a prior "password changed" notice
  if (passInput) { passInput.value = ''; passInput.style.display = ''; }

  if (!val) {
    passRow.style.display = 'none';
    return;
  }

  // v80.7: account logins (@acct:NAME) ALWAYS require a password.
  // No probe call — this is intentional, the auth check happens server-side
  // against the accounts table when the user submits the form.
  // v131.50: the "Forgotten the password? Reset it" link is the STORE device
  // password reset (a manager's PIN). A Head Office / area manager login is a
  // personal account - its password is set in Admin -> Accounts - so the link
  // is hidden there (Matt clicked it while locked out, 11 Sep).
  var srLink = document.getElementById('storeResetLink');
  if (srLink) srLink.style.display = (val.indexOf('@acct:') === 0) ? 'none' : '';
  if (val.indexOf('@acct:') === 0) {
    passRow.style.display = 'block';
    setTimeout(function(){ passInput.focus(); }, 100);
    return;
  }

  // Device unlock is stored as a JSON object {fp: '<hash fingerprint>'} so we can
  // detect password changes. Legacy devices stored just 'true' — we treat that as
  // unlocked-but-unknown-fp and validate against current fp below.
  var deviceKey = 'elcStoreUnlocked_' + val.replace(/\s+/g,'_');
  var stored = localStorage.getItem(deviceKey);
  var storedFp = null;
  var wasUnlocked = false;
  if (stored === 'true') {
    wasUnlocked = true; // legacy format — fp unknown, needs revalidation
  } else if (stored) {
    try { var obj = JSON.parse(stored); if (obj && obj.fp) { storedFp = obj.fp; wasUnlocked = true; } }
    catch(e) {}
  }

  // Probe: does this store have a password set? Also returns current fp.
  try {
    var probe = await DB.auth.verifyStorePassword(val, '__probe__');
    // probe = { verified: bool, fp: string|null }
    // `verified:true` only happens when there's NO password set (the probe password will never
    // actually match a real one). So `verified:false` = password set.
    var hasPassword = !probe.verified;
    if (!hasPassword) {
      // No password set — free access
      passRow.style.display = 'none';
      return;
    }
    // Password IS set. Check if device's stored unlock is still valid.
    if (wasUnlocked && storedFp && storedFp === probe.fp) {
      // Same password as when device was unlocked — skip prompt
      passRow.style.display = 'none';
      return;
    }
    // Either never unlocked, legacy unlock (no fp), or password has changed since unlock.
    // Re-prompt and clear the stale flag.
    if (wasUnlocked) localStorage.removeItem(deviceKey);
    passRow.style.display = 'block';
    setTimeout(function(){ passInput.focus(); }, 100);
  } catch(e) {
    // Can't check — fail open (hide password field so login still works offline)
    passRow.style.display = 'none';
  }
}

async function doLogin(){
  var val=document.getElementById('loginStore').value;
  var err=document.getElementById('loginErr');
  if(!val){err.style.display='block';err.textContent='Please select your store or area';return;}
  err.style.display='none';

  // v80.7: account-based login (Head Office / area managers).
  // Was hardcoded `__area_*` / `__ho` magic strings with no password —
  // now uses real accounts in the database via /api/admin/login.
  // The returned token is stored in sessionStorage.elcAdminToken so that
  // any admin actions taken from the staff app (e.g. cross-store edits
  // that route through requireAdmin) carry the right Bearer header.
  if (val.indexOf('@acct:') === 0) {
    var accountName = val.slice('@acct:'.length);
    var passRow = document.getElementById('storePassRow');
    var passInput = document.getElementById('storePassInput');
    var passErr = document.getElementById('storePassErr');
    var password = passInput ? passInput.value : '';
    if (!password) {
      passRow.style.display = 'block';
      passErr.textContent = 'Password required for ' + accountName;
      passErr.style.display = 'block';
      passInput.focus();
      return;
    }
    var btn = document.getElementById('loginBtn');
    btn.disabled = true; btn.textContent = 'Checking…';
    try {
      var workerUrl = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
      var loginRes = await fetch(workerUrl + '/api/admin/login', { cache: 'no-store',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: accountName, password: password })
      });
      if (!loginRes.ok) {
        var msg = 'Wrong password';
        try { var j = await loginRes.json(); if (j && j.error) msg = j.error; } catch(e) {}
        passErr.textContent = msg;
        passErr.style.display = 'block';
        passInput.value = '';
        passInput.focus();
        btn.disabled = false; btn.textContent = 'Continue';
        return;
      }
      var session = await loginRes.json();
      // session shape: { token, name, role, area, expires_at }
      sessionStorage.setItem('elcAdminToken', session.token);
      // v116.x: also persist to localStorage so the token survives iOS evicting the
      // app mid-visit (sessionStorage is wiped on relaunch; localStorage is not).
      try { localStorage.setItem('elcAdminToken', session.token); localStorage.setItem('elcAdminTokenAt', String(Date.now())); } catch(e) {}   // v131.39: the 12h re-arm ceiling reads the stamp
      // v133.106: which sign-in is the STAFF APP'S OWN. The admin app and chat write the same slot, so when
      // this app ends a session on the server (the 12h ceiling, Sign out) it ends only its own, never theirs.
      try { localStorage.setItem('elcAdminTokenStaff', session.token); } catch(e) {}
      // Best-effort: also expose on window for any code paths that read it
      // synchronously (e.g. older fetch helpers).
      try { window.adminToken = session.token; } catch(e) {}
      // Build initials from the name for the topbar avatar
      var initials = String(session.name || '').split(/\s+/).filter(Boolean)
        .slice(0,2).map(function(p){return p.charAt(0).toUpperCase();}).join('');
      currentUser = {
        name: session.name,
        role: session.role,         // 'admin' or 'area_manager'
        area: session.area || null,
        store: null,                // not tied to a single store
        initials: initials || (session.role === 'admin' ? 'HO' : 'AM')
      };
      // v131.49: Head Office ordered a new password - set it before the app opens.
      if (session.must_reset && typeof DB !== 'undefined' && DB.auth && typeof DB.auth.forcePasswordChange === 'function') {
        var fpcDone = await DB.auth.forcePasswordChange(session);
        if (!fpcDone) {
          try { sessionStorage.removeItem('elcAdminToken'); localStorage.removeItem('elcAdminToken'); localStorage.removeItem('elcAdminTokenAt'); } catch(e) {}
          passErr.textContent = 'A new password is required before you can sign in.';
          passErr.style.display = 'block';
          btn.disabled = false; btn.textContent = 'Continue';
          return;
        }
      }
    } catch(e) {
      passErr.textContent = 'Login failed: ' + (e.message || 'try again');
      passErr.style.display = 'block';
      btn.disabled = false; btn.textContent = 'Continue';
      return;
    }
    btn.disabled = false; btn.textContent = 'Continue';
    isKioskMode = false;
    // v129.99: lsSetSafe — a quota-full device evicts its caches instead of
    // throwing at login (Dar Al Salam, 37 unhandled QuotaExceededErrors).
    lsSetSafe('elcShiftUser', JSON.stringify(currentUser));
    lsSetSafe('elcShiftVersion', 'v49');   // v129.111: raw setItem here threw on a full device and killed the login
    startApp();
    return;
  }

  // Regular store login — check if store has a password set
  // v50: unlock is stored as {fp: '<hash fingerprint>'} so password changes re-prompt.
  {
    var deviceKey = 'elcStoreUnlocked_' + val.replace(/\s+/g,'_');
    var storedRaw = localStorage.getItem(deviceKey);
    var storedFp = null;
    if (storedRaw && storedRaw !== 'true') {
      try { var obj = JSON.parse(storedRaw); if (obj && obj.fp) storedFp = obj.fp; } catch(e) {}
    }

    if (typeof DB !== 'undefined') {
      var passRow = document.getElementById('storePassRow');
      var passInput = document.getElementById('storePassInput');
      var passErr = document.getElementById('storePassErr');
      var password = passInput ? passInput.value : '';

      // Probe current password state — returns {verified, fp}
      // v131.42: the stored fp rides along, so an already-unlocked device gets
      // its store token re-issued without re-typing the password.
      try {
        var probe = await DB.auth.verifyStorePassword(val, '__probe__', storedFp || undefined);
        if (probe && probe.store_token && DB.setStoreToken) DB.setStoreToken(val, probe.store_token);
        var hasPassword = !probe.verified;
        var currentFp = probe.fp;

        // If device has a stored fp matching the current one, skip the prompt
        if (hasPassword && storedFp && storedFp === currentFp) {
          // Still unlocked with current password — continue
        } else if (hasPassword) {
          // Password set and either not unlocked or password has changed → require entry
          if (!password) {
            passRow.style.display = 'block';
            passErr.textContent = 'This store requires a password';
            passErr.style.display = 'block';
            passInput.focus();
            return;
          }
          var btn = document.getElementById('loginBtn');
          btn.disabled = true; btn.textContent = 'Checking…';
          try {
            var verifyResult = await DB.auth.verifyStorePassword(val, password);
            if (!verifyResult.verified) {
              passErr.textContent = 'Wrong password';
              passErr.style.display = 'block';
              passInput.value = '';
              passInput.focus();
              btn.disabled = false; btn.textContent = 'Continue';
              return;
            }
            // Success — remember this device is unlocked with the CURRENT fp
            lsSetSafe(deviceKey, JSON.stringify({fp: verifyResult.fp}));
            if (verifyResult.store_token && DB.setStoreToken) DB.setStoreToken(val, verifyResult.store_token);   // v131.42
          } catch(e) {
            // v136.9: too many tries (this store locked, or this connection) is said as the Worker says it.
            passErr.textContent = (e && e.status === 429 && e.message) ? e.message : 'Error: ' + (e.message || 'try again');
            passErr.style.display = 'block';
            btn.disabled = false; btn.textContent = 'Continue';
            return;
          }
          btn.disabled = false; btn.textContent = 'Continue';
          // Signal successful password verification to Chrome's credential manager
          // so it offers to save. The form's preventDefault blocks the usual heuristic.
          if (window.PasswordCredential) {
            try {
              var cred = new PasswordCredential({
                id: val,
                password: password,
                name: val,
              });
              await navigator.credentials.store(cred);
            } catch(e) { /* non-fatal */ }
          }
        } else {
          // No password set — clear any stale unlock flag so it's consistent
          if (storedRaw) localStorage.removeItem(deviceKey);
        }
      } catch(e) {
        // Can't verify — fail open (let them in; they can't reach the DB anyway)
      }
    }

    currentUser={name:val,role:'staff',store:val,area:null,initials:'ST'};
  }
  isKioskMode=false;
  lsSetSafe('elcShiftUser',JSON.stringify(currentUser));   // v129.99 quota self-healing
  lsSetSafe('elcShiftVersion', 'v49');   // v129.111: raw setItem here threw on a full device and killed the login
  startApp();
}

// v131.48: the Worker refused a read because this till holds no store token
// (or a stale one after a password rotation). Forget the unlock + token, go
// back to the login screen, and say why in words a store can act on. Fires
// at most once per 20s so a burst of parallel reads does not stack alerts.
var _sessionLostAt = 0;
function storeSessionLost(msg) {
  var now = Date.now();
  if (now - _sessionLostAt < 20000) return;
  _sessionLostAt = now;
  try {
    var st = (typeof currentStore !== 'undefined' && currentStore) ? currentStore : '';
    if (st) localStorage.removeItem('elcStoreUnlocked_' + st.replace(/\s+/g, '_'));
    if (typeof DB !== 'undefined' && DB.setStoreToken) DB.setStoreToken(null, null);
  } catch (e) { /* storage unavailable */ }
  try { doLogout(); } catch (e) { /* the login screen show below still runs */ }
  try {
    var le = document.getElementById('loginErr');
    if (le) { le.textContent = 'This till needs the store password again (it was changed). Pick your store and enter the new password.'; le.style.display = 'block'; }
    var ls = document.getElementById('loginScreen'); if (ls) ls.style.display = 'flex';
  } catch (e) { /* cosmetic */ }
}
window.__storeSessionLost = storeSessionLost;

// v133.106: end the staff app's OWN sign-in on the server too. Clearing it on the device left the server
// session live for up to 30 days, and one account signed in daily piled up ~20 (the 12 Oct security digest:
// "684 live sessions for 33 accounts"). Only the token this app signed in with (elcAdminTokenStaff), so the
// admin app and chat stay signed in. Best effort: a failure leaves it to the login-time tidy (login.js).
function endOwnServerSession(tok) {
  try {
    var mine = localStorage.getItem('elcAdminTokenStaff');
    if (!tok || !mine || tok !== mine) return false;
    localStorage.removeItem('elcAdminTokenStaff');
    if (typeof DB !== 'undefined' && DB.workerUrl) {
      fetch(DB.workerUrl() + '/api/admin/logout', { method: 'POST', keepalive: true, cache: 'no-store',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token: tok }) }).catch(function () {});
    }
    return true;
  } catch (e) { return false; }
}
window.__endOwnServerSession = endOwnServerSession;

function doLogout(){
  try { endOwnServerSession(localStorage.getItem('elcAdminToken') || sessionStorage.getItem('elcAdminToken')); } catch(e) {}   // v133.106
  localStorage.removeItem('elcShiftUser');currentUser=null;
  // v116.x: clear the admin/AM token from both stores + window on logout.
  try { localStorage.removeItem('elcAdminToken'); localStorage.removeItem('elcAdminTokenAt'); sessionStorage.removeItem('elcAdminToken'); window.adminToken = null; } catch(e) {}
  document.getElementById('userMenu').classList.remove('open');
  document.getElementById('app').style.display='none';
  document.getElementById('mainTopbar').style.display='none';
  document.getElementById('bottomNav').style.display='none';
  var _fbFabOut=document.getElementById('feedbackFab'); if(_fbFabOut)_fbFabOut.style.display='none';
  // v126.36: the floating concern FAB was removed (concern access stays on the login card + in Comms)
  document.getElementById('loginScreen').style.display='flex';
  // Overnight fix (sensitive): My Development is a full-screen overlay that
  // lives OUTSIDE #app, so hiding #app above does not hide it, and it holds
  // the last person's PIN in memory until closeMyDevelopment() resets it.
  // Close it on every logout. Only the REAL function is called: while the
  // lazy stub is still in place the mydev bundle never loaded, so the overlay
  // cannot be open and there is nothing to close (and no reason to fetch it).
  try {
    if (typeof window.closeMyDevelopment === 'function' && !window.closeMyDevelopment.__stub) window.closeMyDevelopment();
  } catch (e) { /* never block the logout itself */ }
  // Overnight fix (sensitive): forget WHO was verified on this till. The
  // cached PIN (db.js) is what every write is sent as, and the session
  // attribution (index.html) is whose name the app shows on those writes.
  // Both comments always said logout cleared them; nothing did, so the next
  // person's saves could go out as the previous person for up to 8 hours.
  try { if (typeof DB !== 'undefined' && typeof DB.clearAuthPin === 'function') DB.clearAuthPin(); } catch (e) { /* never block the logout */ }
  try { if (typeof clearSessionAttribution === 'function') clearSessionAttribution(); } catch (e) { /* never block the logout */ }
}

// Phase C2 (docs/saas-control-plane-plan.md) — hide feature tabs a tenant's
// plan doesn't include. Driven by DB.hasModule(), which DEFAULTS TO ON when
// there's no org_config (ELC / any tenant without a config) — so ELC shows
// EVERYTHING and this is a no-op for it. Demo Toys (core/comms/badges) loses
// the Calls tab. Purely presentational; the server enforces its own gates.
// Bottom-nav ids → the module each belongs to. Absent from the map = always on
// (core: clock/rota/kpi/toil/checks/reviews/visits/develop — the base product).
var MODULE_NAV = {
  // v128.53 (Stage B): the hub button is gone — every comms surface gates on
  // its own tab now. Links is core chrome (ungated).
  'nav-chat': 'comms', 'nav-email': 'comms', 'nav-news': 'comms', 'nav-tasks': 'comms',
  'nav-calls': 'calls',
  'nav-awards': 'badges',   // v129.0 — the 🏆 Awards tab follows the badges module
};
function applyModuleGating(){
  try {
    if (typeof DB === 'undefined' || typeof DB.hasModule !== 'function') return;
    Object.keys(MODULE_NAV).forEach(function(id){
      var el = document.getElementById(id);
      if (!el) return;
      var on = DB.hasModule(MODULE_NAV[id]);
      // don't fight role-based hiding: only touch display when the module is OFF
      if (!on) el.style.display = 'none';
    });
    // Badges live as a KPI sub-tab + the store shelf, not a nav button.
    if (!DB.hasModule('badges')) {
      ['kpiTabBadges', 'storeBadgeShelf', 'storeBadgeShelfKpi'].forEach(function(id){
        var el = document.getElementById(id);
        if (el) el.style.display = 'none';
      });
    }
  } catch (e) { /* gating is presentational — never block startApp */ }
}
window.applyModuleGating = applyModuleGating;
// v132.129 - the Virtual Store tab (second in the bar). Matt, 1 Oct 2026: "i also wonder if it
// should be its own tab on shift, as i think its hidden". Hidden in the markup; shown only when Head
// Office has switched the game on (GET /api/virtual-store/config, tokenless, no personal data) and
// the tenant has the module (DB.hasModule defaults ON with no org_config, so ELC has it). Fired from
// startApp and never awaited: the boot never waits on it, and any failure leaves the tab hidden.
function showVirtualStoreTab(){
  try {
    var el = document.getElementById('nav-vstore');
    if (!el || typeof DB === 'undefined' || typeof DB.workerUrl !== 'function') return;
    if (typeof DB.hasModule === 'function' && !DB.hasModule('vstore')) return;
    fetch(DB.workerUrl() + '/api/virtual-store/config', { cache: 'no-store' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) { if (d && d.enabled) el.style.display = ''; })
      .catch(function () { /* offline: the tab stays hidden this time */ });
  } catch (e) { /* presentational: never block startApp */ }
}
window.showVirtualStoreTab = showVirtualStoreTab;
// v133.118 - the 📷 Scan tab (scan.html). Hidden in the markup; shown only on a store device signed in to
// its store once Head Office has switched Scan on (admin, Top 50, 📷 Scan). POST /api/scan/status answers
// 403 while it is off; db.js adds the device's X-Store-Token. Never awaited, any failure leaves it hidden.
function showScanTab(){
  try {
    var el = document.getElementById('nav-scan');
    if (!el || typeof DB === 'undefined' || typeof DB.workerUrl !== 'function') return;
    var u = null;
    try { u = JSON.parse(localStorage.getItem('elcShiftUser') || 'null'); } catch (e) { u = null; }
    if (!u || u.role !== 'staff' || !u.store) return;
    fetch(DB.workerUrl() + '/api/scan/status', { cache: 'no-store', method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ store: u.store }) })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) { if (d && d.ok && d.enabled) el.style.display = ''; })
      .catch(function () { /* offline: the tab stays hidden this time */ });
  } catch (e) { /* presentational: never block startApp */ }
}
window.showScanTab = showScanTab;
// Phase C3 (docs/saas-control-plane-plan.md) — apply the tenant's white-label
// skin from org_config.theme. Only an EXPLICIT theme override changes anything:
// a colour override needs theme.primary; the brand text needs theme.logo/label.
// ELC's orgs row carries theme={} (and no org_config at all on prod), so every
// guard below is false → ELC keeps its hardcoded "ELC Shift" green brand,
// byte-identical. Demo Toys (theme primary #1f6feb, logo "🧸 PlayShift") renders
// blue + PlayShift. Cosmetic only — wrapped so it can never block startApp.
function applyTenantBranding(){
  try {
    if (typeof DB === 'undefined' || typeof DB.orgConfig !== 'function') return;
    var cfg = DB.orgConfig();
    if (!cfg) return; // no tenant config → built-in brand
    // v133.109: the operator identity first (brand.js): another tenant never keeps ALGT's logo, the
    // "Operators of" strip or the trademark notice. The explicit theme overrides below still win.
    try { if (typeof applyBrand === 'function') applyBrand(cfg); } catch (e) { /* cosmetic */ }
    var theme = (cfg.theme && typeof cfg.theme === 'object') ? cfg.theme : {};
    var root = document.documentElement;
    if (theme.primary) {
      // the topbar gradient + shadow read --elc-green/--elc-green-dark; --green is
      // the legacy accent some components still use. Point all three at the skin.
      root.style.setProperty('--elc-green', theme.primary);
      root.style.setProperty('--elc-green-dark', theme.primary);
      root.style.setProperty('--green', theme.primary);
    }
    if (theme.accent) root.style.setProperty('--elc-lime', theme.accent);
    // Brand text: an explicit logo/label overrides "ELC Shift" — never
    // product_label alone (that defaults to "Shift" for ELC).
    var brand = theme.logo || theme.label;
    if (brand) {
      var logo = document.querySelector('.topbar-logo');
      if (logo) logo.textContent = brand;
      // v130.28: the LOGIN CARD heading too - it is its own hardcoded element,
      // and a prospect watching a demo video reads this line first.
      // v130.32: the line under the pill is the PRODUCT name, not the brand
      // again - Toyshop Land's card read "Toyshop Land" twice. product_label
      // ('TSL Shift') fills it when the tenant set one; the generic default
      // 'Shift' falls back to the brand so a label-less tenant isn't blank.
      var loginTitle = document.querySelector('.login-title');
      if (loginTitle) {
        loginTitle.textContent = (cfg.product_label && cfg.product_label !== 'Shift')
          ? cfg.product_label : brand;
      }
      // v130.29: the TE + ELC logo IMAGES are hardcoded <img>s in the login
      // card - a white-label tenant must not show another company's marks.
      // The theme logo (emoji + name) takes their place. v130.32: scale to
      // FIT - 34px suits an emoji-only mark, but a name alongside it wrapped
      // inside the white pill; longer marks drop to 24px on one line.
      var loginLogo = document.querySelector('.login-logo');
      // v130.36: the legal links point at ELC/ALGT documents - a white-label
      // tenant repoints them at the generic tenant legal pages (which brand
      // themselves from org_config at load). The concern link STAYS: the
      // channel is org-scoped, so a tenant concern lands with the tenant's
      // own admin - a demoable feature, not a leak.
      var legal = document.getElementById('loginLegalLinks');
      if (legal) {
        var links = legal.querySelectorAll('a[href]');
        links.forEach(function (l) {
          var h = l.getAttribute('href') || '';
          if (h.indexOf('/privacy') === 0) l.href = '/tenant-privacy.html' + location.search;
          if (h.indexOf('/terms') === 0) l.href = '/tenant-terms.html' + location.search;
        });
      }
      if (loginLogo) {
        loginLogo.innerHTML = '';
        loginLogo.textContent = theme.logo || '';
        loginLogo.style.display = theme.logo ? '' : 'none';   // v133.109: applyBrand hid the empty slot
        loginLogo.style.fontSize = (theme.logo && theme.logo.length > 4) ? '24px' : '34px';
        loginLogo.style.lineHeight = '1.2';
        loginLogo.style.whiteSpace = 'nowrap';
      }
    }
    if (theme.label) { try { document.title = theme.label; } catch (e) { /* no document.title */ } }
  } catch (e) { /* branding is cosmetic — never block startApp */ }
}
window.applyTenantBranding = applyTenantBranding;
// Phase C4 (decision #8) — relabel the toy-specific STATIC labels from the
// tenant's taxonomy. DB.term() falls back to the toy word when a key is absent,
// so ELC (empty taxonomy) is byte-identical. Dynamic surfaces (the add-on tally
// headings) call DB.term() at render time; this handles the fixed UI chrome.
function applyTenantTaxonomy(){
  try {
    if (typeof DB === 'undefined' || typeof DB.term !== 'function') return;
    var addons = DB.term('addons', 'Add-ons');
    var demos  = DB.term('demos', 'Demo Line');
    var addonsTab = document.getElementById('kpiTabAddons');
    if (addonsTab) addonsTab.textContent = '🎯 ' + addons;
    var demoLbl = document.getElementById('demoLineLabel');
    if (demoLbl) demoLbl.textContent = '📦 ' + demos;
    // v126.43: the badge-fairness explainer no longer names a currency — demo /
    // add-on badges rank on "% of the store's OWN target" (v126.40), not units per
    // 1,000 AED — so there is nothing currency-shaped left to relabel here.
    // Per-tenant currency still applies everywhere else via formatCurrency/DB.currency().
  } catch (e) { /* taxonomy relabel is cosmetic — never block startApp */ }
}
window.applyTenantTaxonomy = applyTenantTaxonomy;
// Overnight fix (sensitive): run-once flag for the timers and listeners that
// startApp wires (see inside). Module-level, so it lasts the page's lifetime.
var _startAppTimersWired = false;
function startApp(){
  document.getElementById('loginScreen').style.display='none';
  document.getElementById('app').style.display='block';
  document.getElementById('mainTopbar').style.display='flex';
  document.getElementById('bottomNav').style.display='flex';
  applyModuleGating();     // C2: hide tabs for modules this tenant doesn't have
  applyTenantBranding();   // C3: apply the tenant's white-label skin
  applyTenantTaxonomy();   // C4: relabel toy-specific terms from the tenant's taxonomy
  showVirtualStoreTab();   // v132.129: the Virtual Store tab, when it is switched on (not awaited)
  showScanTab();   // v133.118: the Scan tab, when Head Office has switched it on (not awaited)
  // v134.7: the shorter bar (Head Office switch nav_compact, OFF by default; src/ui/compact-nav.js). Not awaited and
  // presentational: it never blocks startApp, and a missing key or failed read leaves the full bar exactly as it is.
  try { if (typeof window.applyCompactNav === 'function') window.applyCompactNav(); } catch (e) { /* never block startApp */ }
  var _fbFab=document.getElementById('feedbackFab'); if(_fbFab)_fbFab.style.display='flex';
  // v126.36: the floating concern FAB was removed (see index.html)
  document.getElementById('tmDate').value=todayStr();
  var sel=document.getElementById('rotaStoreSelect');
  sel.innerHTML='';
  getMyStores().forEach(function(s){var o=document.createElement('option');o.value=s;o.textContent=s;sel.appendChild(o);});
  currentStore=currentUser.store||getMyStores()[0]||'';
  sel.value=currentStore;
  // Hide store selector if user only has access to one store
  sel.style.display=getMyStores().length<=1?'none':'';
  sel.disabled=getMyStores().length<=1;
  // v78: parallel store-switcher on the Logs screen — same options, same currentStore.
  // The Rota dropdown is buried inside the Rota screen, so area managers had no way
  // to switch stores while viewing logs. Both selects are kept in sync via
  // onLogsStoreChange() and onStoreChange().
  var logsSel=document.getElementById('logsStoreSelect');
  if (logsSel) {
    logsSel.innerHTML='';
    getMyStores().forEach(function(s){var o=document.createElement('option');o.value=s;o.textContent=s;logsSel.appendChild(o);});
    logsSel.value=currentStore;
    var logsNav=document.getElementById('logsStoreNav');
    if (logsNav) logsNav.style.display=getMyStores().length<=1?'none':'flex';
  }
  // v126.51 — the TOPBAR store switcher (Matt: HO "are stuck on the 1st store,
  // al seef, it should be drop down"). currentStore falls back to
  // getMyStores()[0] for an account with no home store, which is why HO always
  // landed on the alphabetically-first store; the only way out was the dropdown
  // buried in the Rota screen. Same options, same onStoreChange() — the v78 logs
  // fix generalised, so it works on EVERY screen instead of a third copy.
  // Multi-store only: a single-store store device keeps the plain text label.
  var topSel=document.getElementById('topbarStoreSelect');
  var topTxt=document.getElementById('topbarStore');
  if (topSel) {
    var multi=getMyStores().length>1;
    topSel.innerHTML='';
    getMyStores().forEach(function(s){var o=document.createElement('option');o.value=s;o.textContent=s;topSel.appendChild(o);});
    topSel.value=currentStore;
    topSel.style.display=multi?'':'none';
    if (topTxt) topTxt.style.display=multi?'none':'';
  }
  // Show/hide role-specific features
  var isAreaUser=currentUser.role==='area_manager'||currentUser.role==='admin';
  // AI Analyse button — HO/area only
  var analyseBtn=document.getElementById('aiAnalyseBtn');
  if(analyseBtn)analyseBtn.style.display=isAreaUser?'':'none';
  // Compliance tab — HO/area only
  var clTab=document.getElementById('clTabCompliance');
  if(clTab)clTab.style.display=isAreaUser?'':'none';
  // Clock header title
  if(isAreaUser){
    document.getElementById('clockTitle').textContent=currentUser.role==='admin'?'All Stores — Live Clock':'Area Clock Overview';
    if(document.getElementById('clockPolicyBanner'))document.getElementById('clockPolicyBanner').style.display='none';
  } else {
    // Store user — silently check location permission (no prompt yet, just query state)
    // The banner, if needed, will appear on the clock screen
    if (typeof Clock.checkLocationPermission === 'function') {
      setTimeout(Clock.checkLocationPermission, 100); // small defer so UI paints first
    }
  }
  // Balances subtitle and admin bar
  var isAreaUser=currentUser.role==='area_manager'||currentUser.role==='admin';
  document.getElementById('toilHeaderSub').textContent=isAreaUser
    ?'TOIL · Holiday · Sick — '+(currentUser.role==='admin'?'all stores':'your area')
    :'TOIL · Holiday · Sick — your store';
  var toilBar=document.getElementById('toilAdminBar');
  if(toilBar){
    toilBar.style.display=isAreaUser?'flex':'none';
    if(isAreaUser){
      var tSel=document.getElementById('toilStoreFilter');
      if(tSel&&tSel.options.length<=1){
        getMyStores().forEach(function(st){
          var o=document.createElement('option');o.value=st;o.textContent=st;tSel.appendChild(o);
        });
      }
    }
  }
  var navArea=document.getElementById('nav-area');
  if(navArea)navArea.style.display=isAreaUser?'':'none';
  if(isAreaUser){
    var ast=document.getElementById('areaScreenTitle');
    if(ast)ast.textContent=currentUser.role==='admin'?'All Stores Overview':currentUser.area+' Area Overview';
    var asb=document.getElementById('areaScreenSub');
    if(asb)asb.textContent=currentUser.role==='admin'?'Head Office view — all areas':'Area Manager view — your stores only';
  }
  currentWeekStart=getWeekStart(new Date());
  // Load real shifts from DB for current + next week
  loadShiftsFromDB(currentWeekStart).then(function(){
    renderRotaWeek();
    renderClockScreen();
  }).catch(function(e){ console.warn('[DB] Shift load error:', e.message); });
  // Load delivery schedule from DB so rota view shows delivery bars
  loadDeliveryFromDB().catch(function(e){ console.warn('[DB] Delivery load error:', e.message); });
  // Load today's clock state from DB (so refresh doesn't lose clocked-in status)
  Clock.loadFromDB().then(function(){
    renderClockScreen();
  }).catch(function(e){ console.warn('[DB] Clock load error:', e.message); });
  // Check for past open clock sessions (still-clocked-in banner + the
  // overnight carry). v129.151: this guarded-and-called the bare
  // `checkStillClockedIn`, a name that stopped existing at the v88.0
  // extraction (the bundle exposes it only as Clock.checkStuck) — so the
  // login-time check silently never ran. Guard what you call.
  // v136.8: the sign-in check is FORCED and runs now, before the clock-screen draws below,
  // so those draws (throttled, src/clock/stuck.js) see it in flight and do not ask again:
  // one read per sign-in instead of four (two when today's clock load lands after it has
  // answered, because that load wipes the carried overnight shift and only a fresh check
  // puts it back). It was deferred 200 ms "so the UI paints first",
  // but it only starts a request; nothing waits on it.
  if (typeof Clock !== 'undefined' && typeof Clock.checkStuck === 'function') {
    Clock.checkStuck();
  }
  // v136.5: every sign-in starts Checks and Logs on today. The till is never reloaded, so a day picked yesterday
  // would otherwise still be selected for the next person (the Safe Log "no Add Entry" report, 16 Oct).
  ['clHistoryDate', 'logsHistoryDate'].forEach(function (id) { var el = document.getElementById(id); if (el) el.value = ''; });
  // Load checklist templates so the dashboard can render
  loadChecklistTemplatesFromDB().then(function(){
    renderChecklistDashboard();
  }).catch(function(e){ console.warn('[DB] Checklist templates load error:', e.message); });
  // Load log templates so the logs screen can render its dashboard
  loadLogTemplatesFromDB().then(function(){
    if (typeof renderLogsDashboard === 'function') renderLogsDashboard();
  }).catch(function(e){ console.warn('[DB] Log templates load error:', e.message); });
  updateTopbar();
  renderClockScreen();
  renderRotaWeek();
  renderToil();
  // v128.51 (layout Stage A) — boot lands on 🏠 Today; paint it once the shell is up
  // v136.8: true = a sign-in always reads the day again (a revisit inside a minute does not)
  if (typeof renderTodayScreen === 'function') renderTodayScreen(true);
  // Overnight fix (sensitive): everything from here to the focus listener is
  // wired ONCE per page load. startApp runs on every login, and a till logs
  // in many times a day (the idle logout), so these used to stack: N clock
  // tickers, N "Good morning" toasts at midnight, N refreshes on every
  // return-to-app. Nothing inside reads a per-login value at wiring time:
  // the ticker and the listeners read the live globals (currentUser,
  // currentView, selectedDay) each time they fire.
  if (!_startAppTimersWired) {
  _startAppTimersWired = true;
  // Track current day for midnight rollover
  var _currentDay = new Date().getDate();
  setInterval(function(){
    var now = new Date();
    // v136.8 (the 16 Oct render audit): write only when the text changes (it changes once a minute,
    // not every second), and leave the clock screen's own date line alone while it shows another
    // day ("(history)") or the area view's "Live" line: this used to overwrite both within a second.
    // Decided from state (the history box, the role), never from the words on screen (Arabic).
    var _tbT = document.getElementById('topbarTime');
    var _tbTxt = pad(now.getHours())+':'+pad(now.getMinutes());
    if (_tbT && _tbT.textContent !== _tbTxt) _tbT.textContent = _tbTxt;
    var _cdEl = document.getElementById('clockDate');
    var _cdHist = document.getElementById('clockHistoryDate');
    var _cdArea = !!(currentUser && (currentUser.role === 'area_manager' || currentUser.role === 'admin'));
    if (_cdEl && !(_cdHist && _cdHist.value) && !_cdArea) {
      var _cdTxt = now.toLocaleDateString('en-GB',{weekday:'long',day:'numeric',month:'long',year:'numeric'});
      if (_cdEl.textContent !== _cdTxt) _cdEl.textContent = _cdTxt;
    }
    if (now.getDate() !== _currentDay) {
      _currentDay = now.getDate();
      selectedDay = getDayKey(now);
      Object.keys(Clock.events).forEach(function(k){ delete Clock.events[k]; });  // v87.0: was clockEvents = {} — clear keys instead so the Clock module reference doesn't go stale
      // v136.8: that wipe drops a carried overnight shift too; the next clock draw must read the open sessions again
      if (typeof Clock.forceNextStuckCheck === 'function') Clock.forceNextStuckCheck();
      Clock.loadFromDB().finally(function(){ renderClockScreen(); });
      if (currentView === 'day') renderDayView();
      if (currentView === 'week') renderRotaWeek();
      showToast('Good morning! Clock reset for ' + now.toLocaleDateString('en-GB',{weekday:'long',day:'numeric',month:'long'}), 'success');
    }
  },1000);
  // Sync indicator — check connection status every 10s
  setInterval(updateSyncIndicator,10000);

  // ── Auto-refresh on return-to-app ─────────────────────────────
  // When the user tabs away (or locks phone) and comes back, silently refresh
  // staff/balances/shifts/holidays so admin-side changes show up without a manual reload.
  // Throttled to once per 30s to avoid hammering the DB.
  var _lastVisRefresh = Date.now();
  document.addEventListener('visibilitychange', function(){
    if (document.visibilityState !== 'visible') return;
    if (!currentUser || _idleWarningShown) return; // skip when logged out or warning up
    if (Date.now() - _lastVisRefresh < 30000) return; // throttle
    _lastVisRefresh = Date.now();
    refreshDataSilently();
  });
  // Also refresh on window focus (desktop users Alt-tabbing)
  window.addEventListener('focus', function(){
    if (!currentUser || _idleWarningShown) return;
    if (Date.now() - _lastVisRefresh < 30000) return;
    _lastVisRefresh = Date.now();
    refreshDataSilently();
  });
  }   // end of the run-once block (_startAppTimersWired)

  // ── Idle-timeout logout ─────────────────────────────────────────
  // After 10 minutes of no interaction, show warning modal with 60s countdown.
  // If no response within 60s → full logout.
  // Any tap/keypress/scroll cancels the timer (unless warning is already showing).
  // v132.65 - a link straight to a screen: ?screen=training (or news, tasks...).
  // Deferred one tick so the default screen has painted and the data loads
  // above have started; whitelisted in deep-link.js so an arbitrary parameter
  // can never reach switchScreen; guarded, because a link is a nicety and
  // must never come between a person and clocking in.
  // v136.3 - ?rotastore=NAME (admin's Store rotas, "Edit in the staff app"): switch to that store, but ONLY when it
  // is one this person may already pick (getMyStores, after sign-in); anything else is ignored. Scheduled before the
  // screen hop below, so the rota opens on the right store. Used once, like ?screen=.
  try {
    var _deepStore = rotaStoreFromSearch(location.search, getMyStores());
    if (_deepStore && _deepStore !== currentStore) {
      setTimeout(function () {
        try {
          var _rs = document.getElementById('rotaStoreSelect');
          if (!_rs) return;
          _rs.value = _deepStore;
          if (_rs.value === _deepStore) onStoreChange();   // the option exists: same path as picking it by hand
        } catch (e) {}
      }, 0);
    }
    if (new URLSearchParams(location.search).has('rotastore')) {
      var _ur = new URL(location.href); _ur.searchParams.delete('rotastore');
      history.replaceState(history.state, '', _ur.pathname + (_ur.search || '') + _ur.hash);
    }
  } catch (e) { /* never block startApp */ }
  try {
    var _deepScreen = screenFromSearch(location.search);
    if (_deepScreen && _deepScreen !== 'clock') {
      setTimeout(function () { try { if (typeof window.switchScreen === 'function') window.switchScreen(_deepScreen); } catch (e) {} }, 0);
    }
    // v133.86 (overnight sweep, L): the link is used ONCE. It stayed in the address, and a shared till
    // logs in again without reloading, so every later login landed on that screen instead of Today.
    if (_deepScreen) {
      var _u = new URL(location.href); _u.searchParams.delete('screen');
      history.replaceState(history.state, '', _u.pathname + (_u.search || '') + _u.hash);
    }
  } catch (e) { /* never block startApp */ }
  startIdleTimer();
  loadCoverAreas();   // v136.9: an area handed over for holidays joins this area manager's store pickers (not awaited)
}

// v136.9 (the area hand-over, src/worker/routes/area-handover.js): an area manager covering another area for today
// gets that area's stores in the staff app's store pickers too. Asks the Worker once per start (the admin session's
// Bearer), writes currentUser.cover_area_ids (read by getMyStores in index.html), and refills the pickers only when
// it changed. Never throws and never blocks: a failed read leaves the pickers as they are (own area only), and the
// server decides what may be saved anyway.
function coverAreaIdsFrom(covering) {
  var out = [];
  (Array.isArray(covering) ? covering : []).forEach(function (c) {
    var id = c && c.area_id != null ? String(c.area_id).trim() : '';
    if (id && out.indexOf(id) === -1) out.push(id);
  });
  return out;
}
function refillStorePickers() {
  var stores = getMyStores();
  ['rotaStoreSelect', 'logsStoreSelect', 'topbarStoreSelect'].forEach(function (id) {
    var sel = document.getElementById(id);
    if (!sel) return;
    var keep = sel.value || currentStore;
    sel.innerHTML = '';
    stores.forEach(function (s) { var o = document.createElement('option'); o.value = s; o.textContent = s; sel.appendChild(o); });
    sel.value = keep;
  });
  var rs = document.getElementById('rotaStoreSelect');
  if (rs) { rs.style.display = stores.length <= 1 ? 'none' : ''; rs.disabled = stores.length <= 1; }
  var ts = document.getElementById('topbarStoreSelect'), tt = document.getElementById('topbarStore');
  if (ts) { ts.style.display = stores.length > 1 ? '' : 'none'; if (tt) tt.style.display = stores.length > 1 ? 'none' : ''; }
  var ln = document.getElementById('logsStoreNav');
  if (ln) ln.style.display = stores.length <= 1 ? 'none' : 'flex';
  var tSel = document.getElementById('toilStoreFilter');
  if (tSel) {
    var have = Array.prototype.map.call(tSel.options, function (o) { return o.value; });
    stores.forEach(function (st) { if (have.indexOf(st) === -1) { var o = document.createElement('option'); o.value = st; o.textContent = st; tSel.appendChild(o); } });
  }
}
function loadCoverAreas() {
  try {
    if (!currentUser || currentUser.role !== 'area_manager') return;
    if (typeof DB === 'undefined' || !DB || typeof DB.workerUrl !== 'function') return;
    var tok = sessionStorage.getItem('elcAdminToken') || '';
    if (!tok) return;
    var apply = function (covering) {
      if (!currentUser) return;
      var next = coverAreaIdsFrom(covering);
      var prev = Array.isArray(currentUser.cover_area_ids) ? currentUser.cover_area_ids : [];
      currentUser.cover_area_ids = next;
      if (next.join(',') !== prev.join(',')) refillStorePickers();
    };
    fetch(DB.workerUrl() + '/api/admin/area-handover', { cache: 'no-store', headers: { 'Authorization': 'Bearer ' + tok } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) { apply(d && d.covering); })   // a failed read: own area only (a copy saved earlier is dropped)
      .catch(function () { apply([]); });
  } catch (e) { /* never block startApp */ }
}

function startIdleTimer(){
  if (_idleTimerInitialized) return;
  _idleTimerInitialized = true;
  // Reset on any meaningful interaction
  ['mousedown','touchstart','keydown','scroll','click'].forEach(function(ev){
    document.addEventListener(ev, resetIdleTimer, { passive: true });
  });
  resetIdleTimer();
}

function resetIdleTimer(){
  if (_idleWarningShown) return; // while modal is up, require explicit dismiss
  if (!currentUser) return; // don't run when logged out
  clearTimeout(_idleTimer);
  _idleTimer = setTimeout(showIdleWarning, IDLE_WARN_AFTER_MS);
}

function dismissIdleWarning(){
  _idleWarningShown = false;
  clearTimeout(_idleLogoutTimer);
  clearInterval(_idleCountdownInterval);
  var modal = document.getElementById('idleWarningModal');
  if (modal) modal.style.display = 'none';
  resetIdleTimer();
}

function forceIdleLogout(){
  _idleWarningShown = false;
  clearTimeout(_idleTimer);
  clearTimeout(_idleLogoutTimer);
  clearInterval(_idleCountdownInterval);
  var modal = document.getElementById('idleWarningModal');
  if (modal) modal.style.display = 'none';
  if (typeof doLogout === 'function') doLogout();
}

async function refreshDataSilently(){
  try { await loadAllStaffFromDB(); } catch(e){ return; /* offline — bail quietly */ }
  try { await loadShiftsFromDB(currentWeekStart); } catch(e){ console.warn('[refresh] loadShiftsFromDB failed:', e.message); }
  try { await Clock.loadFromDB(); } catch(e){ console.warn('[refresh] loadClockFromDB failed:', e.message); }
  // Re-render whatever screen is active
  try {
    if (currentScreen === 'rota')  renderCurrentRotaView();
    if (currentScreen === 'clock') renderClockScreen();
    if (currentScreen === 'toil')  renderToil();
    if (currentScreen === 'today' && typeof renderTodayScreen === 'function') renderTodayScreen(true);   // v136.8: a refresh reads again
    if (currentScreen === 'checklist') {
      var isAreaUser = currentUser && (currentUser.role==='area_manager'||currentUser.role==='admin');
      if (isAreaUser) { if (typeof renderAdminChecklistView === 'function') renderAdminChecklistView(); }
      else { if (typeof renderChecklistDashboard === 'function') renderChecklistDashboard(); }
    }
    if (currentScreen === 'logs' && typeof renderLogsDashboard === 'function') renderLogsDashboard();
    if (currentScreen === 'area' && typeof renderAreaScreen === 'function') renderAreaScreen();
  } catch(e) { console.warn('[refresh] render failed:', e.message); }
  dlog('[refresh] silent refresh complete');
}

function updateTopbar(){
  document.getElementById('topbarStore').textContent=currentStore;
  document.getElementById('topbarInitials').textContent=currentUser.initials||initials(currentUser.name);
  document.getElementById('topbarTime').textContent=pad(new Date().getHours())+':'+pad(new Date().getMinutes());
  document.getElementById('umName').textContent=currentUser.name;
  document.getElementById('umRole').textContent={
    admin:'Head Office',
    area_manager:'Area Manager',
    store_manager:'Store Manager',
    staff:'Store Team',
    kiosk:'Kiosk'
  }[currentUser.role]||'';
  updateSyncIndicator();
}

function updateSyncIndicator(){
  var dot=document.getElementById('syncDot');
  if(!dot)return;
  var online=(typeof DB!=='undefined')?DB.isOnline():navigator.onLine;
  dot.style.background=online?'#10b981':'#ef4444';
  dot.style.boxShadow=online?'0 0 0 0 rgba(16,185,129,0.5)':'0 0 0 3px rgba(239,68,68,0.15)';
}

function onStoreChange(){
  currentStore=document.getElementById('rotaStoreSelect').value;
  // v78: keep the parallel logs-screen dropdown in sync
  var logsSel=document.getElementById('logsStoreSelect');
  if (logsSel) logsSel.value=currentStore;
  // v126.51: keep the topbar switcher in sync too, however the store was changed
  var topSel=document.getElementById('topbarStoreSelect');
  if (topSel) topSel.value=currentStore;
  document.getElementById('topbarStore').textContent=currentStore;
  currentWeekStart=getWeekStart(new Date());
  renderClockScreen();renderCurrentRotaView();renderToil();
  // v133.43: fetch the NEW store's week (a store change used to draw the rota and the clock from what was already
  // in memory, so the rota came up blank or stale). Redraw only if the store is still the one asked for.
  var storeAsked=currentStore;
  if(typeof loadShiftsFromDB==='function')loadShiftsFromDB(currentWeekStart).catch(function(){}).then(function(){
    if(currentStore!==storeAsked)return;
    renderClockScreen();
    if(currentScreen==='rota')renderCurrentRotaView();
  });
  // v129.4 (Matt: "when I select a store it stays on the first store until I
  // click another tab and come back") — this hand-kept render list had
  // drifted from the switchScreen ladder and missed Today/KPI/News/Chat/
  // Tasks/etc. Sync KPI's own store select, then RE-RUN THE LADDER for
  // whatever screen is open so the switch repaints in place. Checklist keeps
  // its explicit branch (the ladder's store branch would wipe in-progress
  // answers via clAnswers={}).
  var kpiSel=document.getElementById('kpiStoreSelect');
  if (kpiSel) kpiSel.value=currentStore;
  if (currentScreen === 'checklist') {
    var isAreaUser = currentUser && (currentUser.role==='area_manager'||currentUser.role==='admin');
    if (isAreaUser) { if (typeof renderAdminChecklistView === 'function') renderAdminChecklistView(); }
    else { if (typeof renderChecklistDashboard === 'function') renderChecklistDashboard(); }
  } else if (typeof switchScreen === 'function' && currentScreen) {
    switchScreen(currentScreen);
  }
}

// ── re-expose (preserves the pre-extraction global surface) ──
window.loadAppConfig = loadAppConfig;
window.mapStaffFromDB = mapStaffFromDB;
window.loadAllStaffFromDB = loadAllStaffFromDB;
window.loadStoresFromDB = loadStoresFromDB;
window.populateLoginStores = populateLoginStores;
// v131.37 - self-service store password reset from the login screen.
// Authorised by a MANAGER of the chosen store proving their PIN server-side;
// on success THIS device is unlocked with the new fingerprint (the manager
// just proved who they are) and every other device re-prompts on its next
// load, exactly as when Head Office sets it.
var _srStore = '';   // v131.46: the store the reset was opened FOR - survives a login-list rebuild
function srEsc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
async function openStoreReset() {
  var val = document.getElementById('loginStore').value;
  var errEl = document.getElementById('srErr');
  if (!val || val.indexOf('@acct:') === 0) return;
  _srStore = val;
  document.getElementById('srStore').textContent = val;
  ['srPin', 'srNew', 'srNew2'].forEach(function (id) { var el = document.getElementById(id); if (el) el.value = ''; });
  errEl.style.display = 'none';
  var sel = document.getElementById('srManager');
  sel.innerHTML = '<option value="">Loading…</option>';
  document.getElementById('storeResetModal').classList.add('open');
  try {
    var mgrs = await DB.auth.resetManagers(val);
    sel.innerHTML = '<option value="">— choose the manager —</option>' + mgrs.map(function (m) { return '<option value="' + srEsc(m.id) + '">' + srEsc(m.name) + '</option>'; }).join('');
    if (!mgrs.length) { errEl.textContent = 'No manager is set up for this store yet. Ask your area manager to set the password.'; errEl.style.display = 'block'; }
  } catch (e) {
    sel.innerHTML = '<option value="">— unavailable —</option>';
    errEl.textContent = 'Could not load managers: ' + (e.message || 'try again'); errEl.style.display = 'block';
  }
}
function closeStoreReset() { document.getElementById('storeResetModal').classList.remove('open'); }
async function submitStoreReset() {
  // v131.46: the store the modal was opened for, not whatever the (possibly
  // rebuilt) login list says now; and put the list back to it afterwards.
  var val = _srStore || document.getElementById('loginStore').value;
  var errEl = document.getElementById('srErr');
  if (!val) { errEl.textContent = 'Pick your store first, then open the reset'; errEl.style.display = 'block'; return; }
  var staffId = document.getElementById('srManager').value;
  var pin = document.getElementById('srPin').value.trim();
  var pw1 = document.getElementById('srNew').value;
  var pw2 = document.getElementById('srNew2').value;
  errEl.style.display = 'none';
  var fail = function (m) { errEl.textContent = m; errEl.style.display = 'block'; };
  if (!staffId) return fail('Choose the manager');
  if (!pin) return fail('Enter the manager PIN');
  if (pw1.length < 6) return fail('The new password needs at least 6 characters');
  if (pw1 !== pw2) return fail('The two passwords do not match');
  var btn = document.getElementById('srSubmit');
  btn.disabled = true; btn.textContent = 'Changing…';
  try {
    var r = await DB.auth.resetStorePassword(val, staffId, pin, pw1);
    var deviceKey = 'elcStoreUnlocked_' + val.replace(/\s+/g, '_');
    if (r && r.fp) lsSetSafe(deviceKey, JSON.stringify({ fp: r.fp }));
    if (r && r.store_token && DB.setStoreToken) DB.setStoreToken(val, r.store_token);   // v131.42
    closeStoreReset();
    try { var selRs = document.getElementById('loginStore'); if (selRs && selRs.value !== val) selRs.value = val; } catch (e) { /* cosmetic */ }
    var passRow = document.getElementById('storePassRow');
    var passErr = document.getElementById('storePassErr');
    var passInput = document.getElementById('storePassInput');
    if (passInput) passInput.value = '';
    if (passRow) passRow.style.display = 'none';
    if (passErr) { passErr.style.color = 'var(--ok)'; passErr.textContent = 'Password changed. This device is unlocked - press Continue.'; passErr.style.display = 'block'; passRow.style.display = 'block'; if (passInput) passInput.style.display = 'none'; }
  } catch (e) {
    fail(e.message || 'Reset failed');
  } finally {
    btn.disabled = false; btn.textContent = 'Change password';
  }
}
window.openStoreReset = openStoreReset;
window.closeStoreReset = closeStoreReset;
window.submitStoreReset = submitStoreReset;
window.onStoreSelectChange = onStoreSelectChange;
window.doLogin = doLogin;
window.doLogout = doLogout;
window.startApp = startApp;
window.startIdleTimer = startIdleTimer;
window.resetIdleTimer = resetIdleTimer;
window.dismissIdleWarning = dismissIdleWarning;
window.forceIdleLogout = forceIdleLogout;
window.refreshDataSilently = refreshDataSilently;
window.updateTopbar = updateTopbar;
window.updateSyncIndicator = updateSyncIndicator;
window.onStoreChange = onStoreChange;

})();
